import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronsUpDown,
  CircleDashed,
  ExternalLink,
  FileText,
  Loader2,
  Paperclip,
  X,
} from "lucide-react";
import {
  provisionVmForUserSchema,
  type ProvisionVmForUserForm,
} from "@/schemas/admin-provisioning";
import {
  useProvisionVmForUser,
  useProvisionPreview,
} from "@/hooks/useAdminProvisioning";
import { adminProvisioningService } from "@/services/admin-provisioning.service";
import { useUserTenants } from "@/hooks/useUserQueries";
import { adminOrderService } from "@/services/admin-order.service";
import { adminPaymentService } from "@/services/admin-payment.service";
import {
  VerificationModal,
  type VerificationModalPayment,
} from "@/components/payments/VerificationModal";
import {
  adminUploadService,
  type UploadedFile,
} from "@/services/admin-upload.service";
import { useAdminOSImages } from "@/hooks/useAdminOSImageQueries";
import { useProductLines, useProductLine } from "@/hooks/useProductLines";
import { ORDERABLE_REGIONS } from "@/types/product-line";
import type {
  ProductLineListDto,
  SpecConfigDto,
  SpecRangeDto,
} from "@/types/product-line";
import type {
  ExistingVmCheck,
  ProvisionResourceForUserRequest,
  ProvisionResourceForUserResponse,
} from "@/types/admin-provisioning";
import type {
  AdminOSImage,
  User,
  UpdateByUuidProviderState,
} from "@/types/admin";
import { formatCurrency, fromMinorUnits } from "@/lib/currency";
import {
  ACCEPTED_UPLOAD_TYPES,
  MAX_UPLOAD_SIZE_BYTES,
  partitionValidUploads,
} from "@/lib/uploadValidation";
import { cn } from "@/lib/utils";
import { isValidVmName } from "@/lib/vm-name";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/common/page-header";
import { UserCombobox } from "@/components/common/UserCombobox";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const BILLING_CYCLES = [1, 3, 6, 12, 24, 36];

/** Term the custom-days field opens on when the operator switches away from whole months. */
const DEFAULT_CUSTOM_DAYS = 7;

/** Server-side cap on a day-based term (a longer commitment is a month cycle). */
const MAX_CUSTOM_DAYS = 30;

const MB_PER_GB = 1024;

/** Upper bound on proof files per payment — matches the server-side cap. */
const MAX_PROOF_FILES = 10;

/**
 * The region the client wizard starts on (`getDefaultRegion()` — the one entry flagged
 * `isDefault && available`). Orders are stamped VND and only this region is actually orderable
 * today, so the admin form opens on it too instead of an empty select.
 */
const DEFAULT_REGION = "vn-south-1";

const DEFAULT_VALUES = {
  targetTenantId: "",
  targetUserId: "",
  vmName: "",
  cores: 2,
  memory: 4096,
  disk: 50,
  additionalIps: 1,
  backupCount: 0,
  snapshotCount: 0,
  region: DEFAULT_REGION,
  productLine: "",
  chipType: "",
  templateId: "",
  billingCycleMonths: 1,
  adminNotes: "",
} satisfies ProvisionVmForUserForm;

/** Mirrors the client wizard's `resolveDefaultProductLine`: Cheap, then Eco, then API order. */
const resolveDefaultProductLine = (lines: ProductLineListDto[]) => {
  const matchesTier = (line: ProductLineListDto, tier: "cheap" | "eco") =>
    line.tier.trim().toLowerCase() === tier ||
    line.slug.trim().toLowerCase() === tier;
  return (
    lines.find((line) => matchesTier(line, "cheap")) ??
    lines.find((line) => matchesTier(line, "eco")) ??
    lines[0]
  );
};

const numberFromInput = (raw: string) => (raw === "" ? undefined : Number(raw));

/** Renders MB as a readable GB figure, keeping one decimal only when the value is not whole. */
const mbToGbLabel = (mb?: number) => {
  if (typeof mb !== "number" || Number.isNaN(mb)) return "—";
  const gb = mb / MB_PER_GB;
  return Number.isInteger(gb) ? `${gb}` : gb.toFixed(1);
};

/**
 * Catalog names often already carry the qualifier ("AlmaLinux 9" + version "9", a product line
 * named after its own tier), so append it only when it adds something.
 */
const withQualifier = (name: string, qualifier?: string | null) =>
  qualifier && !name.toLowerCase().includes(qualifier.toLowerCase())
    ? `${name} ${qualifier}`
    : name;

/**
 * Presets occupy the left half of the size row, so they stack rather than spread. A single column
 * keeps each card's label and its "2 vCPU · 4 GB · 40 GB" line on one row and leaves the stack the
 * same height as the fields opposite it; past four presets that stack would overshoot, so they
 * pair up instead.
 */
const presetGridCols = (count: number) =>
  count > 4 ? "grid-cols-2" : "grid-cols-1";

/** Preset labels are authored per locale by the product-line editor; English is the admin default. */
const presetLabel = (labels: Record<string, string> | undefined, key: string) =>
  labels?.en ?? labels?.vi ?? Object.values(labels ?? {})[0] ?? key;

const outOfRange = (value: number | undefined, range?: SpecRangeDto | null) =>
  typeof value === "number" &&
  !!range &&
  (value < range.min || value > range.max);

/**
 * Clamps into a range and snaps to its step — the admin-side twin of the client wizard's
 * `clampSpec`, so both consoles land on values ProductLineSpecValidation accepts.
 */
const clampToRange = (value: number, range?: SpecRangeDto | null) => {
  if (!range) return value;
  const step = Math.max(1, range.step);
  const snapped = range.min + Math.round((value - range.min) / step) * step;
  return Math.min(range.max, Math.max(range.min, snapped));
};

/** Same clamp, but never rounds below the requested value — used to satisfy an OS minimum. */
const raiseToRange = (value: number, range?: SpecRangeDto | null) => {
  if (!range) return value;
  const step = Math.max(1, range.step);
  const snapped =
    range.min + Math.ceil(Math.max(0, value - range.min) / step) * step;
  return Math.min(range.max, Math.max(range.min, snapped));
};

/** Label with the required marker the whole form uses, so "optional" reads as the exception. */
function FieldLabel({
  htmlFor,
  required,
  children,
}: {
  htmlFor?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <Label htmlFor={htmlFor}>
      {children}
      {required && (
        <span className="ml-0.5 text-destructive-ink" aria-hidden="true">
          *
        </span>
      )}
    </Label>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-destructive-ink">{message}</p>;
}

/**
 * Numeric spec field with its unit rendered inside the control. Native spinners are removed
 * because they would sit on top of the unit suffix.
 */
function SpecNumberField({
  id,
  label,
  unit,
  value,
  onChange,
  range,
  hint,
  error,
  outOfRangeMessage,
}: {
  id: string;
  label: string;
  unit: string;
  value: number | undefined;
  onChange: (next: number | undefined) => void;
  range?: SpecRangeDto | null;
  hint?: ReactNode;
  error?: string;
  outOfRangeMessage?: string;
}) {
  const invalid = outOfRange(value, range);
  return (
    <div className="space-y-1.5">
      <FieldLabel htmlFor={id} required>
        {label}
      </FieldLabel>
      <div className="relative">
        <Input
          id={id}
          type="number"
          inputMode="numeric"
          min={range?.min}
          max={range?.max}
          step={range?.step}
          value={value ?? ""}
          onChange={(e) => onChange(numberFromInput(e.target.value))}
          className="pr-14 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />
        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground">
          {unit}
        </span>
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      {invalid && outOfRangeMessage && (
        <p className="text-xs text-warn-ink">{outOfRangeMessage}</p>
      )}
      <FieldError message={error} />
    </div>
  );
}

/**
 * Searchable OS-template picker. The active images are already loaded in full (one page of 100),
 * so the filter runs client-side over name, version, category and OS type — a plain Select made
 * the operator scroll a flat list of every distro to find one.
 */
function OsTemplateCombobox({
  id,
  value,
  onChange,
  images,
  loading,
}: {
  id?: string;
  value: string;
  onChange: (imageId: string) => void;
  images: AdminOSImage[];
  loading?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selected = images.find((o) => o.id === value);
  const term = search.trim().toLowerCase();
  const matches = term
    ? images.filter((o) =>
        [o.name, o.version, o.category, o.osType]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(term)),
      )
    : images;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={loading}
          className={cn(
            // Flat form-control chrome, matching the selects it sits beside.
            "h-9 w-full justify-between rounded-md border-input px-3 font-normal shadow-none",
            "hover:translate-y-0 hover:border-input hover:bg-transparent",
            !selected && "text-placeholder",
          )}
        >
          <span className="truncate">
            {selected
              ? withQualifier(selected.name, selected.version)
              : loading
                ? "Loading images…"
                : "Select OS template"}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-(--radix-popover-trigger-width) p-0"
        align="start"
      >
        <div className="border-b p-2">
          <Input
            placeholder="Filter by name, version or category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
        <div className="max-h-64 overflow-auto p-1">
          {matches.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No images match.
            </div>
          )}
          {matches.map((o) => (
            <button
              key={o.id}
              type="button"
              className={cn(
                "flex w-full cursor-pointer items-center justify-between gap-2 rounded-sm px-3 py-2 text-left text-sm hover:bg-accent",
                o.id === value && "bg-accent",
              )}
              onClick={() => {
                onChange(o.id);
                setOpen(false);
                setSearch("");
              }}
            >
              <span className="min-w-0 truncate">
                {withQualifier(o.name, o.version)}
                {o.category && (
                  <span className="ml-2 text-muted-foreground">
                    {o.category}
                  </span>
                )}
              </span>
              {o.id === value && <Check className="h-4 w-4 shrink-0" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SummaryRow({ label, value }: { label: string; value?: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      {value ? (
        <span className="min-w-0 truncate text-right font-medium">{value}</span>
      ) : (
        <span className="text-right text-muted-foreground">—</span>
      )}
    </div>
  );
}

/**
 * The UUID box and the verdict for "attach an existing VM". The verdict is always about the UUID
 * that was checked: typing a different one hides it, so a stale "found" can never sit under a new
 * value.
 */
function ExistingVmField({
  uuid,
  onUuidChange,
  onCheck,
  checking,
  checkError,
  check,
}: {
  uuid: string;
  onUuidChange: (next: string) => void;
  onCheck: () => void;
  checking: boolean;
  checkError: string | null;
  check: ExistingVmCheck | null;
}) {
  const provider = check?.provider;
  return (
    <div className="space-y-2">
      <FieldLabel htmlFor="existingVmUuid" required>
        VM UUID
      </FieldLabel>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          id="existingVmUuid"
          value={uuid}
          onChange={(e) => onUuidChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onCheck();
            }
          }}
          placeholder="e.g. 2cd5ed90-1b4f-4c3e-9a51-7f0e8d6b2a11"
          className="font-mono sm:max-w-[26rem]"
          autoComplete="off"
          spellCheck={false}
          aria-invalid={
            Boolean(checkError) || (check ? !check.canAttach : undefined)
          }
          aria-describedby={[
            "existingVmUuid-help",
            checkError && "existingVmUuid-error",
            check && !check.canAttach && "existingVmUuid-blocker",
          ]
            .filter(Boolean)
            .join(" ")}
        />
        <Button
          type="button"
          variant="outline"
          onClick={onCheck}
          disabled={checking || uuid.trim() === ""}
          className="shrink-0"
        >
          {checking && <Loader2 className="h-4 w-4 animate-spin" />}
          {checking ? "Checking…" : "Check VM"}
        </Button>
      </div>
      <p id="existingVmUuid-help" className="text-xs text-muted-foreground">
        The machine must already exist on the infrastructure. Checking it fills
        vCPU, memory, disk and IPs from it; change anything that does not fit
        before creating the order.
      </p>

      {checkError && (
        <p id="existingVmUuid-error" className="text-xs text-destructive-ink">
          {checkError}
        </p>
      )}

      {check && !check.canAttach && (
        <div className="flex gap-2 rounded-md border border-warn/30 bg-warn-soft p-3 text-sm text-warn-ink">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="min-w-0 space-y-1">
            <p id="existingVmUuid-blocker" className="break-words">
              {check.blocker?.message}
            </p>
            {check.claimedByResourceId && (
              <Link
                to={`/admin/resources/${check.claimedByResourceId}`}
                className="inline-flex items-center gap-1 text-xs font-medium hover:underline"
              >
                Open {check.claimedByResourceName ?? "that resource"}{" "}
                <ExternalLink className="h-3 w-3" />
              </Link>
            )}
          </div>
        </div>
      )}

      {check?.canAttach && provider && (
        <div className="space-y-2 rounded-md border border-ok/30 bg-ok-soft p-3 text-sm">
          <p className="flex items-center gap-2 font-medium text-ok-ink">
            <CheckCircle2 className="h-4 w-4" />
            VM found — the form was filled from it
          </p>
          <dl className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
            <div className="flex gap-2">
              <dt className="text-muted-foreground">Power state</dt>
              <dd className="font-medium">{check.powerState}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-muted-foreground">IP</dt>
              <dd className="font-mono">{provider.primaryIpAddress ?? "—"}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-muted-foreground">Size</dt>
              <dd className="font-medium">
                {provider.cores} vCPU · {mbToGbLabel(provider.memoryMb)} GB ·{" "}
                {provider.totalDiskGb} GB
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-muted-foreground">Disks</dt>
              <dd>
                {/* Same list the several-disks warning names: 0 GB slots (a CD-ROM drive) left out. */}
                {provider.disks
                  .filter((d) => d.sizeGb > 0)
                  .sort((a, b) => a.device.localeCompare(b.device))
                  .map((d) => `${d.device} ${d.sizeGb} GB`)
                  .join(", ") || "—"}
              </dd>
            </div>
          </dl>
          {check.warnings.length > 0 && (
            <ul className="space-y-1 border-t border-ok/20 pt-2">
              {check.warnings.map((warning) => (
                <li
                  key={warning.code}
                  className="flex gap-1.5 text-xs text-warn-ink"
                >
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>{warning.message}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * An uploaded slip plus a locally rendered preview. The `url` the API returns points at an
 * auth-protected endpoint, so an `<img src>` aimed at it answers 401 — the thumbnail comes from an
 * object URL over the picked File instead, exactly like the client wizard's upload preview.
 */
type ProofFile = UploadedFile & { previewUrl?: string };

export function ProvisionVmForUserPage() {
  const navigate = useNavigate();
  const [result, setResult] = useState<ProvisionResourceForUserResponse | null>(
    null,
  );
  // Kept alongside the form value so the trigger and the summary can name the user even after the
  // combobox's current search page no longer contains them.
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  /** Transfer slips uploaded for this order, attached to the draft payment on submit. */
  const [proofs, setProofs] = useState<ProofFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);

  /**
   * The payment the verification dialog is open for, or null when it is closed. Held as the
   * dialog's own payload rather than read off `result`, because the amount the server compares
   * lives on the payment record — see `handleOpenVerify`.
   */
  const [verifyModalPayment, setVerifyModalPayment] =
    useState<VerificationModalPayment | null>(null);
  const [openingVerify, setOpeningVerify] = useState(false);
  /**
   * Order status re-read after the payment is verified. The status in `result` is the one the order
   * had when it was created (Draft) and never changes again, so showing it after settlement would
   * report a draft order that is already being provisioned.
   */
  const [liveOrderStatus, setLiveOrderStatus] = useState<string | null>(null);

  /**
   * "Attach an existing VM": the order binds to a machine the infrastructure already runs instead
   * of provisioning one, and is created settled and complete.
   */
  const [attachExistingVm, setAttachExistingVm] = useState(false);
  const [existingVmUuid, setExistingVmUuid] = useState("");
  const [existingVmCheck, setExistingVmCheck] =
    useState<ExistingVmCheck | null>(null);
  const [checkingVm, setCheckingVm] = useState(false);
  const [vmCheckError, setVmCheckError] = useState<string | null>(null);
  // The UUID a check answer must still match when it lands — the operator may have typed on.
  const latestVmUuid = useRef("");
  // Object URLs are owned here: revoked when their slip is removed and when the page unmounts.
  const objectUrls = useRef(new Set<string>());

  const releasePreview = (url?: string) => {
    if (url && objectUrls.current.delete(url)) {
      URL.revokeObjectURL(url);
    }
  };

  useEffect(() => {
    const urls = objectUrls.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm<ProvisionVmForUserForm>({
    resolver: zodResolver(provisionVmForUserSchema),
    defaultValues: DEFAULT_VALUES,
  });

  // The machine this order binds to, once checked and attachable. Its figures pre-fill the size, and
  // while it is set nothing else (a line's default preset, its limits, an OS minimum) silently
  // rewrites them: what does not fit is flagged on the field for the operator to change.
  const attachedVm =
    attachExistingVm && existingVmCheck?.canAttach
      ? existingVmCheck.provider
      : null;
  const attachedVmUuid = attachedVm?.vmUuid;

  const osImages = useAdminOSImages({
    status: "Active",
    page: 1,
    pageSize: 100,
  });
  const osImageItems = osImages.data?.data ?? [];

  const productLines = useProductLines({ isActive: true, page: 1, limit: 100 });
  const productLineItems = productLines.data?.data ?? [];

  // Open on a tier instead of an empty select, exactly as the client wizard's step 0 does. Only
  // fills a blank field, so it never overrides a line the operator picked.
  const productLineCount = productLineItems.length;
  useEffect(() => {
    if (productLineCount === 0 || watch("productLine")) return;
    const line = resolveDefaultProductLine(productLineItems);
    if (line) setValue("productLine", line.slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productLineCount, setValue]);

  // The chip types an admin enabled live on the product line DETAIL record (metadata), not on the
  // list DTO, so the detail has to be fetched for whichever line is selected. This is the same
  // source the client VM wizard populates its CPU selector from.
  const selectedProductLine = productLineItems.find(
    (p) => p.slug === watch("productLine"),
  );
  const productLineDetail = useProductLine(selectedProductLine?.id ?? "");
  const enabledChipTypes = productLineDetail.data?.enabledChipTypes ?? [];

  // Spec bounds and presets are the same admin-configured data the customer wizard renders, and
  // ProductLineSpecValidation enforces them server-side — so the form offers exactly what the
  // backend accepts instead of a fixed range that produces an opaque 400.
  const specLimits = productLineDetail.data?.specLimits;
  const coreRange = specLimits?.cores ?? null;
  const memoryGbRange = specLimits?.memoryGb ?? null;
  const diskRange = specLimits?.diskGb ?? null;
  // The form field is in MB (the wire unit) while the limits are configured in GB.
  const memoryRange: SpecRangeDto | null = memoryGbRange
    ? {
        min: memoryGbRange.min * MB_PER_GB,
        max: memoryGbRange.max * MB_PER_GB,
        step: Math.max(1, memoryGbRange.step * MB_PER_GB),
      }
    : null;

  // The IP bound an admin may pick from belongs to the selected product line, not to this form:
  // the same specLimits drive the customer wizard's slider and are what the backend enforces, so a
  // fixed 1..10 here would offer counts the server rejects the moment a line is configured tighter.
  // The fallback matches the schema's coarse bound and only applies while the detail is still
  // loading or a line has no limits configured.
  const ipRange: SpecRangeDto = {
    min: specLimits?.ips?.min ?? 1,
    max: specLimits?.ips?.max ?? 10,
    step: specLimits?.ips?.step ?? 1,
  };

  // Slot ranges default to the platform-wide bound the same way. A tier that sells no backups
  // configures max = 0, which both hides the field and pins the value at 0.
  const backupRange: SpecRangeDto = {
    min: specLimits?.backupCount?.min ?? 0,
    max: specLimits?.backupCount?.max ?? 7,
    step: specLimits?.backupCount?.step ?? 1,
  };
  const snapshotRange: SpecRangeDto = {
    min: specLimits?.snapshotCount?.min ?? 0,
    max: specLimits?.snapshotCount?.max ?? 7,
    step: specLimits?.snapshotCount?.step ?? 1,
  };

  const presets = useMemo(
    () =>
      [...(productLineDetail.data?.specConfigs ?? [])].sort(
        (a, b) => a.sortOrder - b.sortOrder,
      ),
    [productLineDetail.data?.specConfigs],
  );

  // Keep the selected CPU type valid for the current product line: default to the first enabled one
  // and clear it when the line enables none, so the payload never carries a chip the pool mappings
  // cannot resolve.
  const enabledChipTypesKey = enabledChipTypes.join(",");
  useEffect(() => {
    const current = watch("chipType");
    if (enabledChipTypes.length === 0) {
      if (current) {
        setValue("chipType", "");
      }
      return;
    }
    if (!current || !enabledChipTypes.includes(current)) {
      setValue("chipType", enabledChipTypes[0]);
    }
    // watch() is read imperatively here on purpose; re-running on its value would fight the reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledChipTypesKey, setValue]);

  const applyPreset = (preset: SpecConfigDto) => {
    const options = { shouldDirty: true, shouldValidate: true } as const;
    setValue("cores", clampToRange(preset.cores, coreRange), options);
    setValue(
      "memory",
      clampToRange(preset.memoryGb * MB_PER_GB, memoryRange),
      options,
    );
    setValue("disk", clampToRange(preset.diskGb, diskRange), options);
    setValue(
      "additionalIps",
      clampToRange(preset.additionalIps || ipRange.min, ipRange),
      options,
    );
    // Presets carry slot counts too and the client wizard applies them, so parity means applying
    // them here as well rather than silently leaving the field at zero.
    setValue(
      "backupCount",
      clampToRange(preset.backupCount ?? 0, backupRange),
      options,
    );
    setValue(
      "snapshotCount",
      clampToRange(preset.snapshotCount ?? 0, snapshotRange),
      options,
    );
  };

  // Selecting a product line applies its default preset, exactly as the client wizard does when a
  // tier is picked — the operator starts from the tier the catalog markets rather than whatever
  // numbers the previous line left behind.
  const detailProductLineId = productLineDetail.data?.id;
  useEffect(() => {
    if (!detailProductLineId || attachedVm) return;
    const preset = presets.find((p) => p.isDefault) ?? presets[0];
    if (preset) applyPreset(preset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailProductLineId]);

  // A line with tighter limits must not leave a value behind that the backend rejects — the same
  // clamp the client wizard runs whenever the resolved limits change.
  const specLimitsKey = JSON.stringify(specLimits ?? null);
  useEffect(() => {
    if (!specLimits || attachedVm) return;
    const fields = [
      { name: "cores", range: coreRange },
      { name: "memory", range: memoryRange },
      { name: "disk", range: diskRange },
      { name: "additionalIps", range: ipRange },
      { name: "backupCount", range: backupRange },
      { name: "snapshotCount", range: snapshotRange },
    ] as const;
    for (const { name, range } of fields) {
      const current = watch(name);
      if (typeof current !== "number" || !range) continue;
      const clamped = clampToRange(current, range);
      if (clamped !== current) setValue(name, clamped);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specLimitsKey, setValue]);

  const selectedTenantId = watch("targetTenantId");

  // The operator searches for a person; the tenant to bill is then derived from that person's
  // memberships. The backend does not check that the pair is consistent (CreateOrderForResources
  // takes both ids at face value), so offering only the user's own tenants is what keeps the order
  // attached to a tenant the user actually belongs to.
  const userTenantsQuery = useUserTenants(watch("targetUserId"));
  const userTenants = useMemo(
    () => userTenantsQuery.data ?? [],
    [userTenantsQuery.data],
  );

  const provisionMutation = useProvisionVmForUser();

  // ---- Live pricing preview ----------------------------------------------------
  // Rebuild a preview request (debounced) from the VM-spec fields; the preview runs the SAME
  // pricing/expansion/VAT path as a real order, so the breakdown matches the draft payment.
  const vmName = watch("vmName");
  const cores = watch("cores");
  const additionalIps = watch("additionalIps");
  const ipOutOfRange = outOfRange(additionalIps, ipRange);
  const backupCount = watch("backupCount");
  const snapshotCount = watch("snapshotCount");
  const slotOutOfRange =
    outOfRange(backupCount, backupRange) ||
    outOfRange(snapshotCount, snapshotRange);
  const memory = watch("memory");
  const disk = watch("disk");
  const region = watch("region");
  const productLine = watch("productLine");
  const chipType = watch("chipType");
  const templateId = watch("templateId");
  const billingCycleMonths = watch("billingCycleMonths");
  const billingCycleDays = watch("billingCycleDays");
  // A day-based term replaces the month cycle entirely — the month chips stay pinned to 1 so the
  // payload never claims both. Held as its own state rather than derived from the value, so
  // clearing the field to retype it does not yank the input out from under the operator.
  const [customDaysMode, setCustomDaysMode] = useState(
    billingCycleDays != null,
  );
  const isCustomDays = customDaysMode;
  const isMonthCycle = customDaysMode === false;
  const selectedUserId = watch("targetUserId");

  const selectedTenant = userTenants.find(
    (t) => t.tenantId === selectedTenantId,
  );
  const selectedOsImage = osImageItems.find((o) => o.id === templateId);

  // Map the picked user onto a tenant, but only when the answer is unambiguous — a user may belong
  // to several tenants and the order carries whichever id this field holds. Silently defaulting a
  // multi-tenant user would bill the VM to the wrong workspace with nothing on screen saying so, so
  // that case is left for the operator to choose.
  const userTenantKey = userTenants.map((t) => t.tenantId).join(",");
  useEffect(() => {
    const current = watch("targetTenantId");
    if (current && userTenants.some((t) => t.tenantId === current)) return;
    setValue(
      "targetTenantId",
      userTenants.length === 1 ? userTenants[0].tenantId : "",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTenantKey, setValue]);

  // OS images declare a minimum the image needs to run. The API refuses a NEW VM below it
  // (BELOW_TEMPLATE_MINIMUM_SPECS), so that blocks the submit; a machine that already runs is billed
  // as it is and not held to it, so there it stays a warning.
  const osMinimum = selectedOsImage?.minimumSpecs;
  const belowOsMinimum =
    !!osMinimum &&
    ((typeof cores === "number" && cores < osMinimum.cpu) ||
      (typeof memory === "number" && memory < osMinimum.memory) ||
      (typeof disk === "number" && disk < osMinimum.disk));
  const blockedByOsMinimum = belowOsMinimum && !attachExistingVm;

  // Picking an image raises the size to the image's own minimum when the current one would not
  // boot it, staying inside the product line's limits — a line ceiling below the image minimum
  // leaves the warning standing rather than sending specs the backend rejects.
  useEffect(() => {
    const minimum = selectedOsImage?.minimumSpecs;
    if (!minimum || attachedVm) return;
    const axes = [
      { name: "cores", min: minimum.cpu, range: coreRange },
      { name: "memory", min: minimum.memory, range: memoryRange },
      { name: "disk", min: minimum.disk, range: diskRange },
    ] as const;
    for (const { name, min, range } of axes) {
      const current = watch(name);
      if (typeof current !== "number" || current >= min) continue;
      const next = raiseToRange(min, range);
      if (next !== current) setValue(name, next, { shouldDirty: true });
    }
    // detailProductLineId is a dependency because switching lines applies that line's default
    // preset, which can land below the image's minimum; this effect is declared after that one,
    // so it runs on the preset's values rather than the ones it replaced.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, detailProductLineId, setValue]);

  const [previewRequest, setPreviewRequest] =
    useState<ProvisionResourceForUserRequest | null>(null);
  useEffect(() => {
    // Disk is part of the guard because the order validator requires a positive boot disk: with the
    // field cleared or set to 0 the preview would fire on every debounce and answer 400, replacing
    // the price panel with an error the operator has no way to read as "fix the disk field".
    if (
      !selectedTenantId ||
      !productLine ||
      !templateId ||
      !region ||
      !disk ||
      disk < 1
    ) {
      setPreviewRequest(null);
      return;
    }
    const t = setTimeout(() => {
      setPreviewRequest({
        targetTenantId: selectedTenantId,
        targetUserId: selectedUserId || undefined,
        billingCycleMonths,
        billingCycleDays,
        // The order for a machine that already runs is not held to the image's minimums, so its
        // preview must not be either.
        existingVmUuid: attachedVmUuid,
        resourceItems: [
          {
            type: "VirtualMachine",
            // The preview runs the real order validator, so a half-typed or malformed name would
            // answer 400 and replace the price panel with an error about the name — while pricing
            // does not depend on the name at all. Substitute the placeholder and let the field
            // report its own problem.
            name: isValidVmName(vmName ?? "") ? vmName : "preview",
            specifications: {
              cores,
              memory,
              disk,
              additionalIps,
              // Always sent, even at zero. An absent key makes the pricing engine substitute the
              // product line's configured default, which either fails the order (default above the
              // tier's specLimits) or charges slots nobody asked for.
              backupCount,
              snapshotCount,
              region,
              productline: productLine,
              templateId,
              // ProvisionResourceJob resolves pool_name from the NESTED configuration object, not
              // from the top-level keys. Sending it here keeps the previewed tier and the pool the
              // VM will actually be placed on derived from the same product line.
              configuration: {
                productline: productLine,
                ...(chipType && { chipType }),
              },
            },
          },
        ],
      });
    }, 400);
    return () => clearTimeout(t);
  }, [
    selectedTenantId,
    selectedUserId,
    vmName,
    cores,
    memory,
    disk,
    additionalIps,
    backupCount,
    snapshotCount,
    region,
    productLine,
    chipType,
    templateId,
    billingCycleMonths,
    billingCycleDays,
    attachedVmUuid,
  ]);

  const preview = useProvisionPreview(previewRequest);
  const previewCurrency = preview.data?.currency ?? "VND";
  const previewMoney = (n?: number) =>
    formatCurrency(fromMinorUnits(n ?? 0, previewCurrency), previewCurrency);

  // Everything the preview endpoint (and the order itself) needs before it can answer. Listing the
  // gaps beats a generic "select a tenant, product line, OS template and region" sentence.
  const requirements = [
    { label: "User", ok: !!selectedUserId },
    { label: "Tenant", ok: !!selectedTenantId },
    { label: "Product line", ok: !!productLine },
    { label: "Region", ok: !!region },
    { label: "OS template", ok: !!templateId },
    // Validity, not just presence: the order command applies the full hostname rules, so a name
    // with an underscore would leave the submit live and fail server-side.
    { label: "VM name", ok: isValidVmName(vmName ?? "") },
    { label: "Disk", ok: typeof disk === "number" && disk >= 1 },
  ];
  if (attachExistingVm) {
    requirements.push({ label: "Checked VM", ok: !!attachedVm });
  }
  // Only while the custom-days term is selected: an empty field would silently fall back to the
  // month cycle and charge a full month.
  if (customDaysMode) {
    requirements.push({
      label: "Number of days",
      ok: billingCycleDays !== undefined,
    });
  }
  const missing = requirements.filter((r) => !r.ok);
  // The submit stays inert until the order would actually be accepted: every required field
  // answered, no spec sitting outside the product line's bounds, and a new VM not below its image's
  // minimum — the server rejects both.
  const canSubmit =
    missing.length === 0 &&
    !ipOutOfRange &&
    !slotOutOfRange &&
    !blockedByOsMinimum;

  const activePresetKey = presets.find(
    (p) =>
      p.cores === cores &&
      p.memoryGb * MB_PER_GB === memory &&
      p.diskGb === disk &&
      (p.additionalIps || ipRange.min) === additionalIps,
  )?.key;

  const awaitingVerification = result?.paymentStatus === "AwaitingVerification";

  /**
   * The hint under a size field: the machine's own figure (in warning colour once the form bills
   * something else) next to the allowed range. A figure the machine did not report (0) is left out
   * rather than shown as "VM: 0 vCPU".
   */
  const vmFigureHint = (
    vmValue: number,
    vmFigure: string,
    differs: boolean,
    range?: string,
  ) =>
    vmValue > 0 ? (
      <span className={cn(differs && "text-warn-ink")}>
        VM: {vmFigure}
        {differs ? " — billed differently" : ""}
        {range ? ` · ${range}` : ""}
      </span>
    ) : (
      range
    );

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const picked = Array.from(files).slice(0, MAX_PROOF_FILES - proofs.length);

    // Screen out what the API would refuse anyway. Without this an oversized file went all
    // the way to the server and came back as a raw error the operator could not act on.
    const { valid, errors } = partitionValidUploads(picked);
    errors.forEach((message) => toast.error(message));
    if (valid.length === 0) return;

    setUploading(true);
    try {
      for (const file of valid) {
        const uploaded = await adminUploadService.uploadFile(file);

        let previewUrl: string | undefined;
        if (file.type.startsWith("image/")) {
          previewUrl = URL.createObjectURL(file);
          objectUrls.current.add(previewUrl);
        }

        setProofs((current) => [...current, { ...uploaded, previewUrl }]);
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to upload the file.",
      );
    } finally {
      setUploading(false);
    }
  };

  /**
   * Loads the payment this screen just created, then hands it to the shared verification dialog.
   * <p>
   * The dialog is what collects the amount the operator confirms actually arrived, which the
   * server requires. The figure it compares against is read from the PAYMENT rather than from
   * `result.totalAmount`: the summary above renders that one through `fromMinorUnits`, while the
   * server compares against `Payment.Amount` as the payment DTOs carry it. The two agree for VND
   * and would not for a currency with decimals, so the payment is the only safe source.
   */
  const handleOpenVerify = async () => {
    if (!result) return;

    setOpeningVerify(true);
    try {
      const payment = await adminPaymentService.getPaymentDetails(
        result.paymentId,
      );

      setVerifyModalPayment({
        paymentOrderNumber: payment.paymentOrderNumber,
        amount: payment.amount,
        currency: payment.currency,
        qrProvider: payment.qrProvider,
      });
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to load the payment to verify.",
      );
    } finally {
      setOpeningVerify(false);
    }
  };

  const handleVerify = async (
    notes: string | undefined,
    receivedAmount: number,
  ) => {
    if (!result) return;

    setVerifying(true);
    try {
      await adminPaymentService.verifyPayment(result.paymentId, {
        notes: notes ?? "Verified from the provisioning screen.",
        receivedAmount,
      });
      setVerifyModalPayment(null);
      setVerified(true);
      toast.success("Payment verified — the VM is being provisioned.");

      // Verifying settles the order, so the status captured when it was created (Draft) is now
      // wrong. Re-read it once. A failed read keeps that stale value rather than blanking the row:
      // the verification itself succeeded, and the "View order" link leads to the live record.
      try {
        const order = await adminOrderService.getOrderDetails(result.orderId);
        setLiveOrderStatus(order.status);
      } catch {
        /* empty */
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to verify the payment.",
      );
    } finally {
      setVerifying(false);
    }
  };

  const changeExistingVmUuid = (next: string) => {
    setExistingVmUuid(next);
    latestVmUuid.current = next.trim();
    // A verdict belongs to the UUID it was given for. The API answers with the UUID lower-cased, so
    // retyping the same machine in another case keeps it.
    if (
      existingVmCheck &&
      existingVmCheck.vmUuid !== next.trim().toLowerCase()
    ) {
      setExistingVmCheck(null);
    }
    // Any check still in flight is now for a different UUID; its answer will be dropped.
    setCheckingVm(false);
    setVmCheckError(null);
  };

  /**
   * Fills the size from the checked machine, once. Only figures the machine reported are written;
   * memory is rounded up to the whole GB the form edits, and the disk is the total of every disk.
   */
  const prefillFromVm = (vm: UpdateByUuidProviderState) => {
    const options = { shouldDirty: true, shouldValidate: true } as const;
    if (vm.cores > 0) setValue("cores", vm.cores, options);
    if (vm.memoryMb > 0) {
      setValue(
        "memory",
        Math.ceil(vm.memoryMb / MB_PER_GB) * MB_PER_GB,
        options,
      );
    }
    if (vm.totalDiskGb > 0) setValue("disk", vm.totalDiskGb, options);
    setValue("additionalIps", Math.max(1, vm.networks.length), options);
  };

  const handleCheckVm = async () => {
    const uuid = existingVmUuid.trim();
    if (!uuid) return;
    latestVmUuid.current = uuid;
    setCheckingVm(true);
    setVmCheckError(null);
    setExistingVmCheck(null);
    try {
      const check = await adminProvisioningService.checkExistingVm(uuid);
      if (latestVmUuid.current === uuid) {
        setExistingVmCheck(check);
        if (check.canAttach && check.provider) prefillFromVm(check.provider);
      }
    } catch (error) {
      if (latestVmUuid.current === uuid) {
        setVmCheckError(
          error instanceof Error ? error.message : "Failed to check the VM.",
        );
      }
    } finally {
      // A check for a UUID the operator has since replaced must not end the newer check's spinner.
      if (latestVmUuid.current === uuid) setCheckingVm(false);
    }
  };

  const selectVmSource = (existing: boolean) => {
    setAttachExistingVm(existing);
    if (!existing) {
      // Drop any check still in flight too: its answer would otherwise pass the UUID guard and
      // overwrite the size of a new-VM order with a machine nobody is attaching.
      latestVmUuid.current = "";
      setCheckingVm(false);
      setExistingVmCheck(null);
      setVmCheckError(null);
    }
  };

  const handleReset = () => {
    // reset() alone would drop back to a blank product line: the effects that derive the tier, its
    // preset and its chip type only fire when their source changes, and none of them changes here.
    const line = resolveDefaultProductLine(productLineItems);
    reset({ ...DEFAULT_VALUES, productLine: line?.slug ?? "" });
    setCustomDaysMode(false);
    setSelectedUser(null);
    proofs.forEach((proof) => releasePreview(proof.previewUrl));
    setProofs([]);
    setVerified(false);
    setVerifyModalPayment(null);
    setLiveOrderStatus(null);
    setAttachExistingVm(false);
    setExistingVmUuid("");
    latestVmUuid.current = "";
    setCheckingVm(false);
    setExistingVmCheck(null);
    setVmCheckError(null);

    if (line && line.slug === productLine) {
      const preset = presets.find((p) => p.isDefault) ?? presets[0];
      if (preset) applyPreset(preset);
      if (enabledChipTypes.length > 0)
        setValue("chipType", enabledChipTypes[0]);
    }

    setResult(null);
    provisionMutation.reset();
  };

  const onSubmit = (values: ProvisionVmForUserForm) => {
    setResult(null);
    setLiveOrderStatus(null);
    const payload: ProvisionResourceForUserRequest = {
      targetTenantId: values.targetTenantId,
      targetUserId: values.targetUserId || undefined,
      billingCycleMonths: values.billingCycleMonths,
      billingCycleDays: values.billingCycleDays,
      // Any slip uploaded above rides along and lands on the draft payment, which moves it to
      // AwaitingVerification so it can be verified from the rail without leaving this screen.
      attachmentIds:
        proofs.length > 0 ? proofs.map((p) => p.attachmentId) : undefined,
      adminNotes: values.adminNotes || undefined,
      existingVmUuid: attachedVm ? attachedVm.vmUuid : undefined,
      resourceItems: [
        {
          type: "VirtualMachine",
          name: values.vmName,
          specifications: {
            cores: values.cores,
            memory: values.memory,
            disk: values.disk,
            additionalIps: values.additionalIps,
            backupCount: values.backupCount,
            snapshotCount: values.snapshotCount,
            region: values.region,
            productline: values.productLine,
            templateId: values.templateId,
            configuration: {
              productline: values.productLine,
              ...(values.chipType && { chipType: values.chipType }),
            },
          },
        },
      ],
    };

    provisionMutation.mutate(payload, {
      onSuccess: (data) => {
        setResult(data);
        if (attachedVm) {
          if (data.existingVmAttachError && data.vmResourceId) {
            // The money is recorded; only the link to the machine is missing. The VM's page is where
            // "Update resource by UUID" finishes it, so that is the page to land on.
            toast.warning(
              `Order ${data.orderNumber}: ${data.existingVmAttachError} Finish it with "Update resource by UUID" on this page.`,
              { duration: 15000 },
            );
            navigate(`/admin/resources/${data.vmResourceId}`);
            return;
          }
          // Nothing is left to settle: the order is already complete, so it is the page to land on.
          toast.success(
            `Order ${data.orderNumber} completed — the VM is attached.`,
          );
          navigate(`/admin/orders/${data.orderId}`);
          return;
        }
        toast.success(`Order created: ${data.orderNumber}`);
        // The operator's next step is always the payment (verify it, or read its state), so go
        // straight there instead of leaving them on a spent form.
        navigate(`/admin/payments/${data.paymentId}`);
      },
      onError: (error) => {
        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to provision resource.",
        );
      },
    });
  };

  return (
    <div className="w-full space-y-4 py-4">
      <PageHeader
        title="Provision VM for user"
        subtitle={
          attachExistingVm
            ? "Create a settled, completed order for a VM that already runs on the infrastructure, on behalf of a tenant/user."
            : "Create an order, the VM, and a draft payment on behalf of a tenant/user."
        }
      />

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_22rem] xl:grid-cols-[minmax(0,1fr)_24rem]">
        {/* ---------------------------------------------------------------- form */}
        <div className="space-y-4">
          <Card className="shadow-none">
            <CardHeader className="px-4 pb-2 pt-4">
              <CardTitle>Target</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 px-4 pb-4 pt-0 sm:grid-cols-2 xl:grid-cols-3">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="targetUserId" required>
                  User
                </FieldLabel>
                <Controller
                  control={control}
                  name="targetUserId"
                  render={({ field }) => (
                    <UserCombobox
                      id="targetUserId"
                      value={field.value}
                      onChange={(user) => {
                        const nextId = user?.id ?? "";
                        // Re-picking the same user must not clear the tenant: the membership list
                        // is unchanged, so the effect that refills it would never fire again.
                        if (nextId === field.value) return;
                        field.onChange(nextId);
                        setSelectedUser(user);
                        // The tenant is derived from the new user's memberships; clearing it here
                        // keeps the pair consistent while that list loads.
                        setValue("targetTenantId", "");
                      }}
                    />
                  )}
                />
                <FieldError message={errors.targetUserId?.message} />
              </div>

              <div className="space-y-1.5">
                <FieldLabel htmlFor="targetTenantId" required>
                  Tenant
                </FieldLabel>
                <Controller
                  control={control}
                  name="targetTenantId"
                  render={({ field }) => (
                    <Select
                      value={field.value ?? ""}
                      onValueChange={field.onChange}
                      disabled={!selectedUserId || userTenantsQuery.isLoading}
                    >
                      <SelectTrigger id="targetTenantId">
                        <SelectValue
                          placeholder={
                            !selectedUserId
                              ? "Select user first"
                              : userTenantsQuery.isLoading
                                ? "Loading tenants…"
                                : "Select tenant"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {userTenants.map((t) => (
                          <SelectItem key={t.tenantId} value={t.tenantId}>
                            {t.tenantName} · {t.role}
                            {t.isDefault ? " (default)" : ""}
                            {t.tenantStatus !== "Active"
                              ? ` — ${t.tenantStatus}`
                              : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {selectedUserId && !userTenantsQuery.isLoading && (
                  <>
                    {userTenants.length === 0 && (
                      <p className="text-xs text-warn-ink">
                        This user belongs to no tenant, so there is nothing to
                        bill the VM to.
                      </p>
                    )}
                    {userTenants.length > 1 && (
                      <p className="text-xs text-warn-ink">
                        Member of {userTenants.length} tenants — pick the one to
                        bill.
                      </p>
                    )}
                  </>
                )}
                {selectedTenant && selectedTenant.tenantStatus !== "Active" && (
                  <p className="text-xs text-warn-ink">
                    This tenant is {selectedTenant.tenantStatus}.
                  </p>
                )}
                <FieldError message={errors.targetTenantId?.message} />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader className="px-4 pb-2 pt-4">
              <CardTitle>Virtual machine</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 px-4 pb-4 pt-0">
              <div className="space-y-2">
                {/* Radix RadioGroup for arrow-key movement and a roving tabindex; each option is a
                    chip-styled label around its radio, like the other choice rows on this page. */}
                <RadioGroup
                  aria-label="VM source"
                  value={attachExistingVm ? "existing" : "new"}
                  onValueChange={(value: string) =>
                    selectVmSource(value === "existing")
                  }
                  className="flex flex-wrap gap-2"
                >
                  {[
                    { value: "new", label: "Provision a new VM" },
                    { value: "existing", label: "Attach an existing VM" },
                  ].map((option) => {
                    const selected =
                      (attachExistingVm ? "existing" : "new") === option.value;
                    return (
                      <label
                        key={option.value}
                        htmlFor={`vm-source-${option.value}`}
                        className={cn(
                          "flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors",
                          selected
                            ? "border-brand bg-brand-soft font-medium text-brand-ink"
                            : "hover:bg-panel-2",
                        )}
                      >
                        <RadioGroupItem
                          id={`vm-source-${option.value}`}
                          value={option.value}
                          className="cursor-pointer"
                        />
                        {option.label}
                      </label>
                    );
                  })}
                </RadioGroup>
                {attachExistingVm && (
                  <ExistingVmField
                    uuid={existingVmUuid}
                    onUuidChange={changeExistingVmUuid}
                    onCheck={() => void handleCheckVm()}
                    checking={checkingVm}
                    checkError={vmCheckError}
                    check={existingVmCheck}
                  />
                )}
              </div>

              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                <div className="space-y-1.5">
                  <FieldLabel htmlFor="productLine" required>
                    Product line
                  </FieldLabel>
                  <Controller
                    control={control}
                    name="productLine"
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger id="productLine">
                          <SelectValue placeholder="Select product line" />
                        </SelectTrigger>
                        <SelectContent>
                          {productLineItems.map((p) => (
                            <SelectItem key={p.slug} value={p.slug}>
                              {withQualifier(p.name, p.tier)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  <FieldError message={errors.productLine?.message} />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel htmlFor="region" required>
                    Region
                  </FieldLabel>
                  <Controller
                    control={control}
                    name="region"
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger id="region">
                          <SelectValue placeholder="Select region" />
                        </SelectTrigger>
                        <SelectContent>
                          {ORDERABLE_REGIONS.map((r) => (
                            <SelectItem key={r} value={r}>
                              {r}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  <FieldError message={errors.region?.message} />
                </div>

                <div className="space-y-1.5">
                  <FieldLabel htmlFor="templateId" required>
                    OS template
                  </FieldLabel>
                  <Controller
                    control={control}
                    name="templateId"
                    render={({ field }) => (
                      <OsTemplateCombobox
                        id="templateId"
                        value={field.value}
                        onChange={field.onChange}
                        images={osImageItems}
                        loading={osImages.isLoading}
                      />
                    )}
                  />
                  {attachExistingVm && (
                    <p className="text-xs text-muted-foreground">
                      The OS the machine runs — recorded, not installed.
                    </p>
                  )}
                  <FieldError message={errors.templateId?.message} />
                </div>

                {enabledChipTypes.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-sm font-medium leading-none">CPU type</p>
                    <Controller
                      control={control}
                      name="chipType"
                      render={({ field }) => (
                        <div
                          role="group"
                          aria-label="CPU type"
                          className="flex flex-wrap gap-2"
                        >
                          {enabledChipTypes.map((chip) => (
                            <button
                              key={chip}
                              type="button"
                              onClick={() => field.onChange(chip)}
                              className={cn(
                                "cursor-pointer rounded-md border px-3 py-1.5 text-sm transition-colors",
                                field.value === chip
                                  ? "border-brand bg-brand-soft font-medium text-brand-ink"
                                  : "hover:bg-panel-2",
                              )}
                            >
                              {chip}
                            </button>
                          ))}
                        </div>
                      )}
                    />
                  </div>
                )}

                {osMinimum && (
                  <p
                    className={cn(
                      // Shares the CPU-type row rather than claiming one of its own: the chips
                      // leave the rest of that row empty, and the note reads as context for the
                      // image chosen just above.
                      "self-end pb-1.5 text-xs xl:col-span-2",
                      belowOsMinimum
                        ? "text-warn-ink"
                        : "text-muted-foreground",
                    )}
                  >
                    {belowOsMinimum && (
                      <AlertTriangle className="mr-1 inline h-3.5 w-3.5 align-[-2px]" />
                    )}
                    Image minimum: {osMinimum.cpu} vCPU ·{" "}
                    {mbToGbLabel(osMinimum.memory)} GB · {osMinimum.disk} GB
                    {blockedByOsMinimum &&
                      " — raise the size to create a new VM with this image."}
                  </p>
                )}
              </div>

              <div className="grid gap-3">
                <div className="space-y-1.5">
                  <FieldLabel htmlFor="vmName" required>
                    VM name
                  </FieldLabel>
                  <Input
                    id="vmName"
                    {...register("vmName")}
                    placeholder="e.g. web-server-01"
                  />
                  <FieldError message={errors.vmName?.message} />
                </div>
              </div>

              {/* Size, as two columns of equal width: the presets on the left, the exact numbers
                  they write on the right. They are two ways of setting the same thing, so they sit
                  side by side rather than one above the other. */}
              <div className="grid gap-4 lg:grid-cols-2">
                {presets.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-sm font-medium leading-none">Presets</p>
                    <div
                      role="group"
                      aria-label="Size presets"
                      className={cn(
                        "grid gap-2",
                        presetGridCols(presets.length),
                      )}
                    >
                      {presets.map((p) => (
                        <button
                          key={p.key}
                          type="button"
                          onClick={() => applyPreset(p)}
                          className={cn(
                            "cursor-pointer rounded-md border px-3 py-2 text-left text-xs transition-colors",
                            activePresetKey === p.key
                              ? "border-brand bg-brand-soft text-brand-ink"
                              : "hover:bg-panel-2",
                          )}
                        >
                          <span className="block font-medium">
                            {presetLabel(p.labels, p.key)}
                            {p.isDefault && (
                              <span className="ml-1.5 font-normal text-muted-foreground">
                                (default)
                              </span>
                            )}
                          </span>
                          <span className="block text-[11px] text-muted-foreground">
                            {p.cores} vCPU · {p.memoryGb} GB · {p.diskGb} GB
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Two fields per row inside the right column. With no presets to sit beside, the
                  fields take the whole row back. */}
                <div
                  className={cn(
                    "grid gap-3 sm:grid-cols-2",
                    presets.length === 0 && "lg:col-span-2 xl:grid-cols-4",
                  )}
                >
                  <Controller
                    control={control}
                    name="cores"
                    render={({ field }) => (
                      <SpecNumberField
                        id="cores"
                        label="vCPUs"
                        unit="vCPU"
                        value={field.value}
                        onChange={field.onChange}
                        range={coreRange}
                        hint={
                          attachedVm
                            ? vmFigureHint(
                                attachedVm.cores,
                                `${attachedVm.cores} vCPU`,
                                cores !== attachedVm.cores,
                                coreRange
                                  ? `${coreRange.min}–${coreRange.max}`
                                  : undefined,
                              )
                            : coreRange
                              ? `${coreRange.min}–${coreRange.max}`
                              : undefined
                        }
                        error={errors.cores?.message}
                        outOfRangeMessage={
                          coreRange
                            ? `Allowed: ${coreRange.min}–${coreRange.max} vCPU.`
                            : undefined
                        }
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="memory"
                    render={({ field }) => (
                      <SpecNumberField
                        id="memory"
                        label="Memory"
                        unit="GB"
                        // The form field holds MB (the wire unit the order expects); the control
                        // edits GB, which is how the product line's limits, the presets and the
                        // client wizard's slider all express memory.
                        value={
                          typeof field.value === "number"
                            ? field.value / MB_PER_GB
                            : undefined
                        }
                        onChange={(gb) =>
                          field.onChange(
                            gb === undefined
                              ? undefined
                              : Math.round(gb * MB_PER_GB),
                          )
                        }
                        range={memoryGbRange}
                        hint={
                          attachedVm
                            ? vmFigureHint(
                                attachedVm.memoryMb,
                                `${mbToGbLabel(attachedVm.memoryMb)} GB`,
                                memory !== attachedVm.memoryMb,
                                memoryGbRange
                                  ? `${memoryGbRange.min}–${memoryGbRange.max} GB`
                                  : undefined,
                              )
                            : memoryGbRange
                              ? `${memoryGbRange.min}–${memoryGbRange.max} GB`
                              : undefined
                        }
                        error={errors.memory?.message}
                        outOfRangeMessage={
                          memoryGbRange
                            ? `Allowed: ${memoryGbRange.min}–${memoryGbRange.max} GB.`
                            : undefined
                        }
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="disk"
                    render={({ field }) => (
                      <SpecNumberField
                        id="disk"
                        label="Disk"
                        unit="GB"
                        value={field.value}
                        onChange={field.onChange}
                        range={diskRange}
                        hint={
                          attachedVm
                            ? vmFigureHint(
                                attachedVm.totalDiskGb,
                                `${attachedVm.totalDiskGb} GB (all disks)`,
                                disk !== attachedVm.totalDiskGb,
                                diskRange
                                  ? `${diskRange.min}–${diskRange.max} GB`
                                  : undefined,
                              )
                            : diskRange
                              ? `${diskRange.min}–${diskRange.max} GB`
                              : undefined
                        }
                        error={errors.disk?.message}
                        outOfRangeMessage={
                          diskRange
                            ? `Allowed: ${diskRange.min}–${diskRange.max} GB.`
                            : undefined
                        }
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="additionalIps"
                    render={({ field }) => (
                      <SpecNumberField
                        id="additionalIps"
                        label="Public IPs"
                        unit="IP"
                        value={field.value}
                        onChange={field.onChange}
                        range={ipRange}
                        hint={
                          attachedVm
                            ? vmFigureHint(
                                attachedVm.networks.length,
                                `${attachedVm.networks.length} interface${attachedVm.networks.length > 1 ? "s" : ""}`,
                                additionalIps !== attachedVm.networks.length,
                                `${ipRange.min}–${ipRange.max}`,
                              )
                            : `${ipRange.min}–${ipRange.max}`
                        }
                        error={errors.additionalIps?.message}
                        outOfRangeMessage={
                          ipOutOfRange
                            ? `Allowed: ${ipRange.min}–${ipRange.max}. The server will reject the order.`
                            : undefined
                        }
                      />
                    )}
                  />

                  {/* Slot fields appear only where the tier sells them. A line that caps a slot at 0
                    keeps the field hidden and the value pinned at 0 — which is still SENT, because
                    an absent key makes the engine substitute the configured default instead. */}
                  {backupRange.max > 0 && (
                    <Controller
                      control={control}
                      name="backupCount"
                      render={({ field }) => (
                        <SpecNumberField
                          id="backupCount"
                          label="Backups"
                          unit="slot"
                          value={field.value}
                          onChange={field.onChange}
                          range={backupRange}
                          hint={`${backupRange.min}–${backupRange.max}, billed per disk GB`}
                          error={errors.backupCount?.message}
                          outOfRangeMessage={`Allowed: ${backupRange.min}–${backupRange.max}.`}
                        />
                      )}
                    />
                  )}

                  {snapshotRange.max > 0 && (
                    <Controller
                      control={control}
                      name="snapshotCount"
                      render={({ field }) => (
                        <SpecNumberField
                          id="snapshotCount"
                          label="Snapshots"
                          unit="slot"
                          value={field.value}
                          onChange={field.onChange}
                          range={snapshotRange}
                          hint={`${snapshotRange.min}–${snapshotRange.max}, billed per disk GB`}
                          error={errors.snapshotCount?.message}
                          outOfRangeMessage={`Allowed: ${snapshotRange.min}–${snapshotRange.max}.`}
                        />
                      )}
                    />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-none">
            <CardHeader className="px-4 pb-2 pt-4">
              <CardTitle>Billing &amp; payment</CardTitle>
              <CardDescription>
                {attachExistingVm
                  ? "The payment is recorded as settled and the order completes at once — nothing is provisioned. Adjust the expiry on the resource afterwards if needed."
                  : "A draft (Pending) payment is always created."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 px-4 pb-4 pt-0">
              <div className="space-y-1.5">
                <p className="text-sm font-medium leading-none">
                  Billing cycle
                </p>
                <Controller
                  control={control}
                  name="billingCycleMonths"
                  render={({ field }) => (
                    <div
                      role="group"
                      aria-label="Billing cycle"
                      className="flex flex-wrap gap-2"
                    >
                      {BILLING_CYCLES.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => {
                            field.onChange(c);
                            // Months and days are mutually exclusive on the wire — picking a
                            // month chip drops any custom term.
                            setCustomDaysMode(false);
                            setValue("billingCycleDays", undefined, {
                              shouldValidate: true,
                            });
                          }}
                          className={cn(
                            "cursor-pointer rounded-md border px-3 py-1.5 text-sm transition-colors",
                            isMonthCycle && field.value === c
                              ? "border-brand bg-brand-soft font-medium text-brand-ink"
                              : "hover:bg-panel-2",
                          )}
                        >
                          {c} month{c > 1 ? "s" : ""}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          // A day-based term prices at cycle = 1 (pro-rated), so pin the months
                          // chip back to 1 as the operator switches.
                          field.onChange(1);
                          setCustomDaysMode(true);
                          setValue(
                            "billingCycleDays",
                            billingCycleDays ?? DEFAULT_CUSTOM_DAYS,
                            { shouldValidate: true },
                          );
                        }}
                        className={cn(
                          "cursor-pointer rounded-md border px-3 py-1.5 text-sm transition-colors",
                          isCustomDays
                            ? "border-brand bg-brand-soft font-medium text-brand-ink"
                            : "hover:bg-panel-2",
                        )}
                      >
                        Custom days
                      </button>
                    </div>
                  )}
                />
                {isCustomDays && (
                  <div className="space-y-1.5 pt-1">
                    <Label htmlFor="billingCycleDays">Number of days</Label>
                    <div className="flex items-center gap-2">
                      <Controller
                        control={control}
                        name="billingCycleDays"
                        render={({ field }) => (
                          <Input
                            id="billingCycleDays"
                            type="number"
                            min={1}
                            max={MAX_CUSTOM_DAYS}
                            className="w-32"
                            value={field.value ?? ""}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value === ""
                                  ? undefined
                                  : Number(e.target.value),
                              )
                            }
                          />
                        )}
                      />
                      <span className="text-sm text-muted-foreground">
                        1–{MAX_CUSTOM_DAYS} days · priced pro-rata from the
                        monthly rate
                      </span>
                    </div>
                    <FieldError message={errors.billingCycleDays?.message} />
                  </div>
                )}
              </div>

              {/* Optional. With a slip attached the draft payment becomes verifiable straight from
                  the rail; without one it stays a plain draft to be settled from the Payments
                  screen — VerifyPaymentCommandHandler refuses a payment carrying no proof. */}
              <div className="space-y-1.5">
                <Label htmlFor="proofFiles">Payment proof (optional)</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <label
                    htmlFor="proofFiles"
                    className={cn(
                      "inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors",
                      uploading || proofs.length >= MAX_PROOF_FILES
                        ? "cursor-not-allowed text-ink-3"
                        : "hover:bg-panel-2",
                    )}
                  >
                    <Paperclip className="h-4 w-4" />
                    {uploading ? "Uploading…" : "Attach files"}
                  </label>
                  <input
                    id="proofFiles"
                    type="file"
                    multiple
                    accept={ACCEPTED_UPLOAD_TYPES}
                    className="sr-only"
                    disabled={uploading || proofs.length >= MAX_PROOF_FILES}
                    onChange={(e) => {
                      void handleUpload(e.target.files);
                      // Clear the input so re-picking the same file fires change again.
                      e.target.value = "";
                    }}
                  />
                  <span className="text-xs text-muted-foreground">
                    PNG, JPG or PDF · up to{" "}
                    {MAX_UPLOAD_SIZE_BYTES / (1024 * 1024)}MB each · up to{" "}
                    {MAX_PROOF_FILES} files
                  </span>
                </div>

                {proofs.length > 0 && (
                  <ul className="grid gap-2 sm:grid-cols-2">
                    {proofs.map((proof) => (
                      <li
                        key={proof.attachmentId}
                        className="flex items-center gap-3 rounded-md border bg-panel-2 p-2"
                      >
                        {proof.previewUrl ? (
                          <img
                            src={proof.previewUrl}
                            alt={proof.fileName}
                            className="h-14 w-14 shrink-0 rounded object-cover"
                          />
                        ) : (
                          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded bg-panel">
                            <FileText className="h-6 w-6 text-muted-foreground" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs font-medium">
                            {proof.fileName}
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            {Math.max(1, Math.round(proof.size / 1024))} KB
                          </p>
                        </div>
                        <button
                          type="button"
                          aria-label={`Remove ${proof.fileName}`}
                          onClick={() => {
                            releasePreview(proof.previewUrl);
                            setProofs((current) =>
                              current.filter(
                                (p) => p.attachmentId !== proof.attachmentId,
                              ),
                            );
                          }}
                          className="cursor-pointer text-muted-foreground hover:text-destructive-ink"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="adminNotes">Admin notes (optional)</Label>
                <Textarea
                  id="adminNotes"
                  {...register("adminNotes")}
                  rows={2}
                />
                <FieldError message={errors.adminNotes?.message} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ------------------------------------------------------- summary rail */}
        <aside className="space-y-3 lg:sticky lg:top-0">
          {/* The payment lands here instead of sending the operator to the Payments menu: they just
              created it and attached the slip, so verifying is the next click, not a new journey.
              Verifying settles the order (PaymentVerifiedEvent -> OrderPaidEvent) and that is what
              provisions the VM — the reason this screen no longer provisions anything itself. */}
          {result && (
            <Card
              className={cn(
                "shadow-none",
                verified
                  ? "border-ok/30 bg-ok-soft"
                  : "border-info/30 bg-info-soft",
              )}
            >
              <CardHeader className="px-4 pb-2 pt-4">
                <CardTitle
                  className={cn(
                    "flex items-center gap-2",
                    verified ? "text-ok-ink" : "text-info-ink",
                  )}
                >
                  <Check className="h-4 w-4" />
                  {verified ? "Payment verified" : "Order created"}
                </CardTitle>
                <CardDescription>
                  {verified
                    ? "The order is settled and the VM is being provisioned."
                    : awaitingVerification
                      ? "Proof attached. Verify to settle the order and start the VM."
                      : "Draft payment created. Attach proof to verify it, or settle it from the Payments screen."}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 px-4 pb-4 pt-0 text-sm">
                <SummaryRow label="Order" value={result.orderNumber} />
                <SummaryRow
                  label="Order status"
                  value={liveOrderStatus ?? result.orderStatus}
                />
                <SummaryRow
                  label="Payment"
                  value={verified ? "Completed" : result.paymentStatus}
                />
                <SummaryRow
                  label="Total"
                  value={formatCurrency(
                    fromMinorUnits(
                      result.totalAmount,
                      result.currency ?? "VND",
                    ),
                    result.currency ?? "VND",
                  )}
                />
                {proofs.length > 0 && (
                  <SummaryRow
                    label="Proof"
                    value={`${proofs.length} file${proofs.length > 1 ? "s" : ""}`}
                  />
                )}

                {!verified && awaitingVerification && (
                  <Button
                    type="button"
                    className="mt-1 w-full"
                    onClick={handleOpenVerify}
                    disabled={verifying || openingVerify}
                  >
                    {openingVerify
                      ? "Loading…"
                      : verifying
                        ? "Verifying…"
                        : "Verify payment"}
                  </Button>
                )}

                <div className="flex flex-wrap gap-3 pt-1">
                  <Link
                    to={`/admin/orders/${result.orderId}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-brand-ink hover:underline"
                  >
                    View order <ExternalLink className="h-3 w-3" />
                  </Link>
                  <Link
                    to={`/admin/payments/${result.paymentId}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-brand-ink hover:underline"
                  >
                    View payment <ExternalLink className="h-3 w-3" />
                  </Link>
                  {result.resourceIds[0] && (
                    <Link
                      to={`/admin/resources/${result.resourceIds[0]}`}
                      className="inline-flex items-center gap-1 text-xs font-medium text-brand-ink hover:underline"
                    >
                      View resource <ExternalLink className="h-3 w-3" />
                    </Link>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="shadow-none">
            <CardHeader className="px-4 pb-2 pt-4">
              <CardTitle>Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 px-4 pb-4 pt-0 text-sm">
              <div className="space-y-1.5">
                {attachExistingVm && (
                  <SummaryRow
                    label="Existing VM"
                    value={
                      attachedVm ? (
                        <span className="font-mono">{attachedVm.vmUuid}</span>
                      ) : undefined
                    }
                  />
                )}
                <SummaryRow label="User" value={selectedUser?.fullName} />
                <SummaryRow label="Tenant" value={selectedTenant?.tenantName} />
                <SummaryRow
                  label="Product line"
                  value={selectedProductLine?.name}
                />
                <SummaryRow label="Region" value={region} />
                <SummaryRow
                  label="OS"
                  value={
                    selectedOsImage
                      ? withQualifier(
                          selectedOsImage.name,
                          selectedOsImage.version,
                        )
                      : undefined
                  }
                />
                {chipType && <SummaryRow label="CPU" value={chipType} />}
                <SummaryRow
                  label="Size"
                  value={
                    <span className="font-medium">
                      {cores ?? "—"} vCPU · {mbToGbLabel(memory)} GB ·{" "}
                      {disk ?? "—"} GB · {additionalIps ?? "—"} IP
                      {backupCount ? ` · ${backupCount} backup` : ""}
                      {snapshotCount ? ` · ${snapshotCount} snapshot` : ""}
                    </span>
                  }
                />
                <SummaryRow
                  label="Cycle"
                  value={
                    isCustomDays
                      ? `${billingCycleDays ?? "—"} day${(billingCycleDays ?? 0) > 1 ? "s" : ""}`
                      : `${billingCycleMonths} month${billingCycleMonths > 1 ? "s" : ""}`
                  }
                />
              </div>

              <div className="border-t pt-3">
                {missing.length > 0 ? (
                  <div className="space-y-1.5">
                    <p className="text-xs text-muted-foreground">
                      Still needed:
                    </p>
                    <ul className="space-y-1">
                      {missing.map((r) => (
                        <li
                          key={r.label}
                          className="flex items-center gap-2 text-xs text-muted-foreground"
                        >
                          <CircleDashed className="h-3.5 w-3.5 shrink-0" />
                          {r.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : preview.isLoading ? (
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-5 w-1/2" />
                  </div>
                ) : preview.isError ? (
                  <p className="text-xs text-destructive-ink">
                    Could not compute price
                    {preview.error instanceof Error
                      ? `: ${preview.error.message}`
                      : ""}
                    .
                  </p>
                ) : preview.data ? (
                  <div
                    className={cn(
                      "space-y-2 transition-opacity",
                      preview.isFetching && "opacity-60",
                    )}
                  >
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span>
                        {previewMoney(
                          preview.data.subtotal ?? preview.data.totalAmount,
                        )}
                      </span>
                    </div>
                    {(preview.data.discountAmount ?? 0) > 0 && (
                      <div className="flex justify-between text-ok-ink">
                        <span>Commitment discount</span>
                        <span>
                          −{previewMoney(preview.data.discountAmount)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        {/* Order.TaxRate is already a percent (10 = 10%) — MoneyCalculator
                            divides by 100 — so it must not be scaled again here. */}
                        VAT
                        {preview.data.taxRate != null
                          ? ` (${preview.data.taxRate}%)`
                          : ""}
                      </span>
                      <span>{previewMoney(preview.data.taxAmount)}</span>
                    </div>
                    <div className="flex items-baseline justify-between border-t pt-2">
                      <span className="font-semibold">
                        Total (
                        {isCustomDays
                          ? `${billingCycleDays ?? "—"} day${(billingCycleDays ?? 0) > 1 ? "s" : ""}`
                          : `${billingCycleMonths} mo`}
                        )
                      </span>
                      <span className="text-base font-semibold">
                        {previewMoney(preview.data.totalAmount)}
                      </span>
                    </div>
                    {billingCycleMonths > 1 && (
                      <p className="text-right text-xs text-muted-foreground">
                        ≈{" "}
                        {previewMoney(
                          Math.round(
                            preview.data.totalAmount / billingCycleMonths,
                          ),
                        )}{" "}
                        / month
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Adjust the spec to compute a price.
                  </p>
                )}
              </div>

              <div className="space-y-2 border-t pt-3">
                <Button
                  type="button"
                  className="w-full"
                  onClick={handleSubmit(onSubmit)}
                  disabled={!canSubmit || provisionMutation.isPending}
                >
                  {provisionMutation.isPending
                    ? "Creating…"
                    : attachExistingVm
                      ? "Create order for this VM"
                      : "Create VM"}
                </Button>
                <Button
                  variant="ghost"
                  type="button"
                  className="w-full"
                  onClick={handleReset}
                  disabled={!isDirty && !result}
                >
                  Reset
                </Button>
              </div>
            </CardContent>
          </Card>
        </aside>
      </div>

      {/* Rendered only once the payment has been read, so the dialog always has a real figure to
          check the operator's entry against rather than a placeholder. */}
      {verifyModalPayment && (
        <VerificationModal
          open
          onOpenChange={(open) => {
            if (!open) setVerifyModalPayment(null);
          }}
          payment={verifyModalPayment}
          onConfirm={handleVerify}
          isLoading={verifying}
        />
      )}
    </div>
  );
}

export default ProvisionVmForUserPage;
